import React from 'react';
import styled from 'styled-components';
import { enums, prefixObjectValues, referrerIsOdyssei } from '../../../utils';
import envConfig from '../../../utils/env-config';
import useAsyncHandler from '../../../hooks/use-async-handler';
import { LoadingScreen } from '../loading-screen';
import { LocalLanding } from './local-landing';
import { RemoteLanding } from './remote-landing';
import { LandingDataProvider } from './landing-data-provider';

interface LandingProps {
  keyId?: string;
}

const domIdsStatic = {
  rootNode: 'landing',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const Container = styled.div`
  height: 100%;
`;

/**
 * Routes the "/" landing experience based on the running environment.
 * On localhost the developer-only {@link LocalLanding} is shown; every
 * deployed environment renders {@link RemoteLanding}.
 *
 * When running locally a preview toggle is exposed so the deployed
 * {@link RemoteLanding} can be exercised without leaving localhost. Loading
 * locally with the `?a` querystring (the path `/app` redirects to when it
 * detects an active outage) also forces {@link RemoteLanding} so the outage
 * splash can be tested.
 *
 * Visitors arriving from the login entity (Odyssei) likely still have an
 * active session, so they are sent straight to the app instead.
 */
const Landing: (p: LandingProps) => React.ReactElement = (p) => {
  const domIds = domIdsUnique(p.keyId);
  const forcedByQueryString = new URLSearchParams(window.location.search).has(
    'a',
  );
  const [previewRemote, setPreviewRemote] = React.useState(forcedByQueryString);
  const {
    data: fromLogin,
    error,
    started,
    startRequest,
  } = useAsyncHandler<boolean>(referrerIsOdyssei);

  if (!started) {
    startRequest();
  }

  if (fromLogin) {
    window.location.assign(enums.AppPaths.appLoginDirective);
    return <LoadingScreen />;
  }

  // wait for the referrer check so a logged-in user doesn't see a flash of the
  // landing page before redirecting
  if (fromLogin === undefined && !error) {
    return <LoadingScreen />;
  }

  const showRemote = !envConfig.isLocal || previewRemote;

  return (
    <Container id={domIds.rootNode}>
      {envConfig.isLocal && (
        <button
          id="landing-preview-toggle"
          type="button"
          className="tw-fixed tw-top-2 tw-right-2 tw-z-50 tw-rounded tw-border tw-border-solid tw-border-unm-interactive01-700 tw-bg-white tw-px-3 tw-py-1 tw-text-sm tw-text-unm-interactive01-700 tw-cursor-pointer"
          onClick={() => setPreviewRemote((prev) => !prev)}
        >
          {previewRemote ? 'Show Local Landing' : 'Preview Remote Landing'}
        </button>
      )}
      {showRemote ? (
        <LandingDataProvider>
          <RemoteLanding />
        </LandingDataProvider>
      ) : (
        <LocalLanding />
      )}
    </Container>
  );
};

export default Landing;
